import React, { memo, startTransition, useEffect } from 'react';
import { Object3D, Vector3 } from 'three';

import { Html } from '@react-three/drei';
import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { useSelection } from '@spazious/dollhouse-engine';
import { FNodeTypesEnum, FurnitureNode, getNodeFromObject } from '@spazious/dollhouse-fsetup';
import { FShallowNode } from '@spazious/dollhouse-shallow-graph';
import { useStaging, useStore } from '@spazious/dollhouse-store';
import { styled } from '@spazious/dollhouse-theme';
import { ToolModeEnum } from '@spazious/dollhouse-types';
import { Typography } from '@spazious/dollhouse-uikit';
import { useEventCallback, useForceUpdate } from '@spazious/react-hooks';

const LabelWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 1.75rem;
  min-width: 1.75rem;
  border-radius: 50%;
  font-size: var(--spz-sys-typescale-label-medium-font-size);
  background-color: var(--spz-sys-color-panel);
  border: 2px solid var(--spz-sys-color-on-surface-variant);
`;

const getWorldPositionWithRotation = (
  originator: any,
  position: any,
  height: number = 0.5
): { position: Vector3 } => {
  const rotationAngle = -originator?.rotation || 0;
  const originatorX = originator?.position?.[0] ?? 0;
  const originatorY = originator?.position?.[1] ?? 0;

  const rotatedX = position[0] * Math.cos(rotationAngle) - position[1] * Math.sin(rotationAngle);
  const rotatedY = position[0] * Math.sin(rotationAngle) + position[1] * Math.cos(rotationAngle);

  return { position: new Vector3(rotatedX + originatorX, height, rotatedY + originatorY) };
};

export interface NumberingControlsBaseProps {}

export const NumberingControlsBase: React.FC<NumberingControlsBaseProps> = memo(() => {
  const graph = useStore(({ graph }) => graph);
  const { selected } = useSelection();
  const { staging, createAction } = useStaging();

  const forceUpdate = useForceUpdate();
  const nodes: FurnitureNode[] = graph
    .flatten()
    .filter(({ type, visible }: any) => type === FNodeTypesEnum.Furniture && visible) as any;
  const { chairNumberingCounter, tableNumberingCounter } = nodes.reduce(
    (acc, node) => {
      if (!node.selector) {
        return acc;
      }

      const shallowNode = graph.shallow.nodes[node.selector];
      const numbering = (shallowNode as any)?.meta?.numbering;
      if (!numbering) {
        return acc;
      }
      if (/chair/.test(node.furnitureType)) {
        acc.chairNumberingCounter = Math.max(acc.chairNumberingCounter, numbering);
      } else if (/table/.test(node.furnitureType)) {
        acc.tableNumberingCounter = Math.max(acc.tableNumberingCounter, numbering);
      }
      return acc;
    },
    {
      chairNumberingCounter: 0,
      tableNumberingCounter: 0,
    }
  );
  useEffect(() => graph.subscribe(forceUpdate), [forceUpdate, graph]);

  const updateNumbering = useEventCallback((selected: Object3D[]) => {
    let chairCounter = 1;
    let tableCounter = 1;
    let value = 0;

    const numberedNodes = selected.reduce((acc, object) => {
      const selectedNode: FurnitureNode | null = getNodeFromObject(object) as any;
      if (!selectedNode?.selector) {
        return acc;
      }
      const shallowNode: FShallowNode | null = graph.shallow.nodes[selectedNode.selector];
      const numbering = (shallowNode as any)?.meta?.numbering;
      if (
        selectedNode.type === FNodeTypesEnum.Furniture &&
        !numbering &&
        nodes.some((node) => node.selector === selectedNode.selector)
      ) {
        return [...acc, selectedNode];
      }
      return acc;
    }, [] as FurnitureNode[]);
    if (numberedNodes.length) {
      staging.prepare(true);

      numberedNodes.forEach((node) => {
        if (/chair/.test(node.furnitureType)) {
          value = chairNumberingCounter + chairCounter++;
        } else if (/table/.test(node.furnitureType)) {
          value = tableNumberingCounter + tableCounter++;
        }

        staging.add(createAction('update', node.selector!, 'meta.numbering', value));
      });
      staging.commit(CommanderActionTypesEnum.Meta);
    }
  });
  useEffect(() => startTransition(() => updateNumbering(selected)), [selected, updateNumbering]);

  return nodes.map((node) => {
    if (!node.selector) {
      return null;
    }
    const shallowNode: FShallowNode | null = graph.shallow.nodes[node.selector] ?? null;
    const { position, originator, selector } = node;
    const positionRotated = getWorldPositionWithRotation(originator, position);
    const numbering = (shallowNode as any)?.meta?.numbering;

    if (!numbering) {
      return null;
    }

    return (
      <Html
        key={selector}
        ref={(node) => {
          if (node?.parentElement) {
            node.parentElement.style.pointerEvents = 'none';
          }
        }}
        center
        occlude={false}
        position={positionRotated.position ? positionRotated.position : new Vector3(0, 0, 0)}
      >
        <LabelWrapper>
          <Typography size="small">{numbering}</Typography>
        </LabelWrapper>
      </Html>
    );
  });
});

export const NumberingControls = memo(() => {
  const tool = useStore(({ tool }) => tool);

  if (tool !== ToolModeEnum.Numbering) {
    return null;
  }
  return <NumberingControlsBase />;
});
